import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";

const testimonials = [
  {
    quote:
      "Millennium Infotech provided us with project-based consultants who were nothing short of exceptional. Their expertise was evident in the swift turnaround and thorough understanding of our project needs. Partnering with them allowed us to meet our project goals ahead of schedule and set new standards for quality in our industry.",
    name: "Mary J. Hollister",
    role: "Entrepreneur",
  },
  {
    quote:
      "Thanks to Millennium Infotech, we transformed our data processing capabilities. Their data experts customized solutions using Power BI that enabled us to gain deeper insights and make data-driven decisions more effectively than ever before. The impact on our operational efficiency has been tremendous.",
    name: "Roy Phipps",
    role: "Marketing Manager",
  },
  {
    quote:
      "We faced numerous challenges with our ERP implementation until we engaged the specialists from Millennium Infotech. Their team not only stabilized our systems but also optimized our processes across multiple departments. Their ongoing support and expertise have been crucial in maintaining our ERP systems to adapt to our evolving business needs.",
    name: "Paul N. Jeter",
    role: "Developer",
  },
  {
    quote:
      "The digital transformation services provided by Millennium Infotech helped us modernize our entire IT infrastructure. Their strategic planning and seamless implementation enabled us to stay competitive and agile in a rapidly changing market.",
    name: "Susan M.",
    role: "CTO",
  },
  {
    quote:
      "Partnering with Millennium Infotech for Generative AI solutions was a game-changer for us. Their innovative approach and tailored AI models significantly boosted our operational efficiency and decision-making processes.",
    name: "James Hoff",
    role: "CEO",
  },
  {
    quote:
      "Millennium Infotech's data analysis services have been invaluable. Their insights and visualization tools transformed our raw data into strategic assets, driving informed decisions and uncovering new opportunities for growth.",
    name: "Michael Lang",
    role: "Head of Analytics",
  },
];

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 lg:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Testimonials</span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
            Word from our client
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-500">
            Our clients' success stories reflect the impact and excellence of our technology and professional
            services.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
            <div className="flex -ml-5">
              {testimonials.map((t, i) => (
                <div
                  key={t.name}
                  className="min-w-0 pl-5 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
                >
                  <figure
                    data-testid={`testimonial-card-${i + 1}`}
                    className="h-full rounded-xl bg-slate-50 border border-slate-100 p-8 flex flex-col"
                  >
                    <div className="flex gap-1" aria-label="5 star rating">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={15} className="fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <blockquote className="mt-5 text-sm md:text-base text-slate-600 leading-relaxed flex-1">
                      {t.quote}
                    </blockquote>
                    <figcaption className="mt-8 flex items-end justify-between gap-4">
                      <div>
                        <span className="block font-heading font-semibold text-slate-900">{t.name}</span>
                        <span className="block text-xs text-slate-400 mt-1">{t.role}</span>
                      </div>
                      <Quote size={40} className="text-miti-blue shrink-0" fill="currentColor" strokeWidth={0} />
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex justify-center gap-2.5" data-testid="testimonials-dots">
            {snaps.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonials-dot-${i + 1}`}
                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                aria-label={`Go to testimonials page ${i + 1}`}
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  i === selected ? "w-8 bg-miti-blue" : "w-2.5 bg-slate-200 hover:bg-slate-300"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
